// Pure stats utilities used by the z-score scoring engine. No domain knowledge,
// no factor concepts — just numerics. Kept as a separate module so they can be
// independently tested and so scoring.ts stays focused on flow control rather
// than primitive math.

export function computeStats(values: (number | null)[]): { mean: number; std: number } | null {
  const valid = values.filter((v): v is number => v !== null && isFinite(v));
  if (valid.length < 5) return null;
  const mean = valid.reduce((a, b) => a + b, 0) / valid.length;
  const std = Math.sqrt(valid.reduce((a, b) => a + (b - mean) ** 2, 0) / valid.length);
  return { mean, std: std || 1 };
}

export function zscoreVal(val: number | null, stats: { mean: number; std: number } | null): number | null {
  if (val === null || !stats) return null;
  return (val - stats.mean) / stats.std;
}

export function clamp(v: number | null, lo = -3, hi = 3): number | null {
  if (v === null) return null;
  return Math.min(Math.max(v, lo), hi);
}

// Winsorize raw factor values at P1/P99 to reduce outlier influence before z-scoring.
// Factors with extreme skew (ROE, fwd growth, buyback yield) benefit most.
export function winsorize(values: (number | null)[]): (number | null)[] {
  const valid = values.filter((v): v is number => v !== null && isFinite(v));
  if (valid.length < 20) return values;
  const sorted = [...valid].sort((a, b) => a - b);
  const p1 = sorted[Math.floor(sorted.length * 0.01)];
  const p99 = sorted[Math.floor(sorted.length * 0.99)];
  return values.map(v => {
    if (v === null || !isFinite(v)) return v;
    return Math.min(Math.max(v, p1), p99);
  });
}

export function avgZ(zscores: (number | null)[]): number | null {
  const valid = zscores.filter((v): v is number => v !== null);
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}
